/* eslint-disable @atlassian/relay/graphql-naming */
import { useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { parseISO } from 'date-fns';
import { evaluateRule } from '@atlassian/jira-issue-table-conditional-formatting/src/services/evaluate-rule/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { formattingColors } from '@atlassian/jira-issue-table-conditional-formatting/src/common/utils/formatting-colors/index.tsx';
import type { FieldValue } from '@atlassian/jira-issue-table-conditional-formatting/src/services/evaluate-rule/types.tsx';
import { useIsConditionalFormattingEnabled } from '../../../controllers/features/selectors.tsx';
import type { useCellsConditionalFormatting$key } from './__generated__/useCellsConditionalFormatting.graphql';
import type { useCellsConditionalFormatting_rules$key } from './__generated__/useCellsConditionalFormatting_rules.graphql';
import type { useCellsConditionalFormatting_savedView_rules$key } from './__generated__/useCellsConditionalFormatting_savedView_rules.graphql';
import type { EvaluatedRule, FormattingColorWithIndex } from './types.tsx';

const emptyObject = {};

export const useCellsConditionalFormatting = (
	project: useCellsConditionalFormatting_rules$key | null | undefined,
	fieldSets: useCellsConditionalFormatting$key | null | undefined,
	savedViewFormattingRules?: useCellsConditionalFormatting_savedView_rules$key | null | undefined,
) => {
	const isConditionalFormattingEnabled =
		// eslint-disable-next-line react-hooks/rules-of-hooks
		fg('jira_list_conditional_formatting') && useIsConditionalFormattingEnabled();

	const isSavedViewConditionalFormattingEnabled =
		fg('saved_list_view_technical_changes') && isConditionalFormattingEnabled;

	// eslint-disable-next-line @atlassian/relay/no-wrapping-hooks
	const fields = useFragment<useCellsConditionalFormatting$key>(
		graphql`
			fragment useCellsConditionalFormatting on JiraIssueFieldSetConnection {
				edges {
					node {
						fieldSetId
						fields {
							edges {
								node {
									__typename
									fieldId
									... on JiraSingleLineTextField {
										text
									}
									... on JiraComponentsField {
										selectedComponentsConnection(first: 1000) {
											edges {
												node {
													componentId
												}
											}
										}
									}
									... on JiraLabelsField {
										selectedLabelsConnection(first: 1000) {
											edges {
												node {
													name
												}
											}
										}
									}
									... on JiraSingleSelectUserPickerField {
										user {
											accountId
											id
										}
									}
									... on JiraMultipleSelectUserPickerField {
										selectedUsersConnection {
											edges {
												node {
													accountId
												}
											}
										}
									}
									... on JiraDatePickerField {
										date
									}
									... on JiraDateTimePickerField {
										dateTime
									}
									... on JiraNumberField {
										number
									}
									... on JiraPriorityField {
										priority {
											priorityId
										}
									}
									... on JiraProjectField {
										project {
											projectId
										}
									}
									... on JiraSprintField {
										selectedSprintsConnection {
											edges {
												node {
													sprintId
												}
											}
										}
									}
									... on JiraSingleVersionPickerField {
										version {
											versionId
										}
									}
									... on JiraIssueTypeField {
										issueType {
											issueTypeId
										}
									}
									... on JiraStatusField {
										status {
											statusId
										}
									}
									... on JiraMultipleVersionPickerField {
										selectedVersionsConnection(first: 1000) {
											edges {
												node {
													versionId
												}
											}
										}
									}
									... on JiraSingleSelectField {
										selectedValue {
											id
										}
									}
									... on JiraMultipleSelectField {
										selectedValues {
											edges {
												node {
													id
												}
											}
										}
									}
									... on JiraRadioSelectField {
										selectedOption {
											label: value
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		isConditionalFormattingEnabled ? fieldSets : null,
	);

	// eslint-disable-next-line @atlassian/relay/no-wrapping-hooks
	const rulesOld = useFragment<useCellsConditionalFormatting_rules$key>(
		graphql`
			fragment useCellsConditionalFormatting_rules on JiraProject {
				conditionalFormattingRules(first: 20) {
					edges {
						node {
							expression {
								... on JiraFormattingSingleValueOperand {
									fieldId
									singleValueOperator: operator
									value
								}
								... on JiraFormattingMultipleValueOperand {
									fieldId
									multipleValueOperator: operator
									values
								}
								... on JiraFormattingNoValueOperand {
									fieldId
									noValueOperator: operator
								}
							}
							formattingArea
							formattingColor {
								id
								colorKey
							}
							id
						}
					}
				}
			}
		`,
		isConditionalFormattingEnabled ? (project ?? null) : null,
	);

	// eslint-disable-next-line @atlassian/relay/no-wrapping-hooks
	const rulesNew = useFragment<useCellsConditionalFormatting_savedView_rules$key>(
		graphql`
			fragment useCellsConditionalFormatting_savedView_rules on JiraFormattingRuleConnection {
				edges {
					node {
						expression {
							... on JiraFormattingSingleValueOperand {
								fieldId
								singleValueOperator: operator
								value
							}
							... on JiraFormattingMultipleValueOperand {
								fieldId
								multipleValueOperator: operator
								values
							}
							... on JiraFormattingNoValueOperand {
								fieldId
								noValueOperator: operator
							}
						}
						formattingArea
						formattingColor {
							id
							colorKey
						}
						id
					}
				}
			}
		`,
		isSavedViewConditionalFormattingEnabled ? savedViewFormattingRules : null,
	);

	const rules = isSavedViewConditionalFormattingEnabled
		? rulesNew
		: rulesOld?.conditionalFormattingRules;

	const valuesByFieldId: Map<string, FieldValue> | null = useMemo(() => {
		if (!isConditionalFormattingEnabled) return null;
		if (rules?.edges == null || rules?.edges.length === 0) {
			return null;
		}

		if (fields?.edges == null || fields?.edges?.length === 0) {
			return null;
		}

		const localMap = new Map();
		fields.edges.forEach((fieldSet) =>
			fieldSet?.node?.fields?.edges?.forEach((fieldEdge) => {
				const field = fieldEdge?.node;
				switch (field?.__typename) {
					case 'JiraSingleLineTextField':
						localMap.set(field.fieldId, field.text);
						break;
					case 'JiraLabelsField': {
						const names =
							field.selectedLabelsConnection?.edges
								?.map((edge) => edge?.node?.name)
								.filter((name: string | undefined | null): name is string => name != null) ?? [];
						localMap.set(field.fieldId, names);
						break;
					}
					case 'JiraComponentsField': {
						const ids =
							field.selectedComponentsConnection?.edges
								?.map((edge) => edge?.node?.componentId)
								.filter(
									(componentId: string | undefined): componentId is string => componentId != null,
								) ?? [];
						localMap.set(field.fieldId, ids);
						break;
					}
					case 'JiraSingleSelectUserPickerField': {
						localMap.set(field.fieldId, field.user?.accountId ?? 'UNASSIGNED');
						break;
					}
					case 'JiraDatePickerField': {
						const parsedDate = field.date ? parseISO(field.date) : undefined;
						localMap.set(field.fieldId, parsedDate);
						break;
					}
					case 'JiraDateTimePickerField': {
						const parsedDate = field.dateTime ? parseISO(field.dateTime) : undefined;
						localMap.set(field.fieldId, parsedDate);
						break;
					}
					case 'JiraNumberField': {
						localMap.set(field.fieldId, field.number);
						break;
					}
					case 'JiraPriorityField': {
						localMap.set(field.fieldId, field.priority?.priorityId);
						break;
					}
					case 'JiraProjectField': {
						localMap.set(field.fieldId, field.project?.projectId);
						break;
					}
					case 'JiraSingleVersionPickerField': {
						localMap.set(field.fieldId, field.version?.versionId);
						break;
					}
					case 'JiraIssueTypeField': {
						localMap.set(field.fieldId, field.issueType?.issueTypeId);
						break;
					}
					case 'JiraStatusField': {
						localMap.set(field.fieldId, field.status?.statusId);
						break;
					}
					case 'JiraMultipleSelectUserPickerField': {
						const ids =
							field.selectedUsersConnection?.edges
								?.map((edge) => edge?.node?.accountId)
								.filter(
									(componentId: string | undefined): componentId is string => componentId != null,
								) ?? [];
						localMap.set(field.fieldId, ids);
						break;
					}
					case 'JiraSprintField': {
						const ids =
							field.selectedSprintsConnection?.edges
								?.map((edge) => edge?.node?.sprintId)
								.filter((sprintId: string | undefined): sprintId is string => sprintId != null) ??
							[];
						localMap.set(field.fieldId, ids);
						break;
					}
					case 'JiraMultipleVersionPickerField': {
						const ids =
							field.selectedVersionsConnection?.edges
								?.map((edge) => edge?.node?.versionId)
								.filter(
									(versionId: string | undefined): versionId is string => versionId != null,
								) ?? [];
						localMap.set(field.fieldId, ids);
						break;
					}
					case 'JiraSingleSelectField': {
						localMap.set(field.fieldId, field.selectedValue?.id);
						break;
					}
					case 'JiraMultipleSelectField': {
						const ids =
							field.selectedValues?.edges
								?.map((edge) => edge?.node?.id)
								.filter(
									(selectedValueId: string | undefined): selectedValueId is string =>
										selectedValueId != null,
								) ?? [];
						localMap.set(field.fieldId, ids);
						break;
					}
					case 'JiraRadioSelectField': {
						localMap.set(field.fieldId, field.selectedOption?.label);
						break;
					}

					// TODO: Add all supported types value mapping
					default:
						break;
				}
			}),
		);

		return localMap;
	}, [fields, isConditionalFormattingEnabled, rules]);

	return useMemo((): Record<string, FormattingColorWithIndex> => {
		if (!isConditionalFormattingEnabled) {
			return emptyObject;
		}
		/*
			Evaluate all rules.
			Get all rules before first row rule that's active.
			groupBy fieldId first rule that is active for that fieldId.  {[fieldId]: {value, hoverValue}}
		*/
		const formattingRules = rules?.edges;
		if (
			!formattingRules ||
			formattingRules.length === 0 ||
			fields?.edges == null ||
			fields?.edges.length === 0
		) {
			return emptyObject;
		}

		if (valuesByFieldId == null) {
			return emptyObject;
		}

		let hasActiveRowRule = false;
		const fieldWithActiveRule = new Set<string>();

		const evaluatedRules = Object.fromEntries(
			formattingRules
				.map<[string, EvaluatedRule] | null>((rule, index) => {
					if (hasActiveRowRule) {
						// We dont want to evaluate more rules when
						// an active Row rule exists
						return null;
					}
					const expression = rule?.node?.expression;
					const fieldId = expression?.fieldId;
					const value = expression?.value ?? expression?.values;
					const formattingArea = rule?.node?.formattingArea;
					const operator =
						expression?.noValueOperator ??
						expression?.singleValueOperator ??
						expression?.multipleValueOperator;

					if (
						!fieldId ||
						!operator ||
						(formattingArea === 'CELL' && fieldWithActiveRule.has(fieldId))
					) {
						// FieldId and Operator are mandatory for evaluation
						// First rule per fieldId will be used anything else should not be evaluated
						return null;
					}
					const fieldValue = valuesByFieldId.get(fieldId);

					const active = evaluateRule(operator, fieldValue, value);
					if (!active) {
						return null;
					}
					if (formattingArea === 'ROW') {
						hasActiveRowRule = true;
						return null;
					}

					fieldWithActiveRule.add(fieldId);
					return [
						fieldId,
						{
							index,
							id: fieldId,
							color: formattingColors[rule?.node?.formattingColor?.colorKey ?? ''],
							type: 'CELL',
						},
					];
				})
				.filter(
					(maybeRule: [string, EvaluatedRule] | null): maybeRule is [string, EvaluatedRule] =>
						maybeRule != null,
				),
		);

		return Object.fromEntries(
			fields.edges
				.map<[string, FormattingColorWithIndex] | null>((fieldSetEdge) => {
					const fieldSet = fieldSetEdge?.node;
					if (fieldSet?.fieldSetId == null) {
						return null;
					}

					let rule: EvaluatedRule | undefined;
					fieldSet.fields?.edges?.forEach((fieldEdge) => {
						if (fieldEdge?.node == null) {
							return;
						}
						const maybeRule = evaluatedRules[fieldEdge.node.fieldId];

						if (maybeRule != null && maybeRule.index < (rule?.index ?? Infinity)) {
							rule = maybeRule;
						}
						return null;
					});

					if (rule?.color == null) {
						return null;
					}

					return [fieldSet.fieldSetId, { ...rule.color, index: rule.index }];
				})
				.filter((maybeRule): maybeRule is [string, FormattingColorWithIndex] => maybeRule != null),
		);
	}, [isConditionalFormattingEnabled, rules, fields, valuesByFieldId]);
};
